'use client'

import { useState } from 'react'
import { toast } from 'sonner'
import { Date_formatVerbose } from 'utils/date'
import TogglePremiumButton from './PremiumToggleButton'
import { IconAlertTriangle, IconInfo, IconCheckmark, IconClose } from 'components/icons'
import LoadingAnimation from 'components/layout/LoadingAnimation'
import { Button } from 'components/ui/button'

interface UserPremiumStatusComponentProps {
    premiumStatus: IPremiumStatus | null
    isLoading: boolean
    statusError: string | null
    fetchPremiumStatus: (force?: boolean) => Promise<void>
    texts: {
        cancelPremium: string
        userPremiumStatusNotAvailableTitle: string
        userPremiumRetryLoadingPremiumStatus: string
        errorLoadingUserPremiumStatus: string
        userPremiumStatusNotActive: string
        getPremium: string
        userPremiumStatusActive: string
        userPremiumStatusExpiring: string
        userPremiumStatusCanceled: string
        userPremiumStatusExpired: string
        expiryMessageInactive: string
        subscriptionMessageInactive: string
        expiryMessageActive: string
        subscriptionMessageActive: string
        expiryMessageExpiring: string
        subscriptionMessageExpiring: string
        expiryMessageCanceling: string
        subscriptionMessageCanceling: string
        expiryMessageCanceled: string
        subscriptionMessageCanceled: string
        expiryMessageExpired: string
        subscriptionMessageExpired: string
        buttonTextUpdatePayment: string
        buttonTextRenewSubscription: string
        userPremiumUnsubscribeConfirmationTitle: string
        areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod: string
        keepPremium: string
        userPremiumUnsubscribeConfirmationConfirm: string
        premiumSubscriptionCancelledSuccessfully: string
        paymentProviderRedirect: string
        errorResumingSubscription: string
        errorSubscribing: string
        errorUnsubscribing: string
        notLoggedIn: string
    }
    lang: string
}

interface StatusConfig {
    icon: React.JSX.Element
    text: string
    color: string
    containerClass: string
    expiryKey: string
    subscriptionKey: string
    buttonText: string
    smallPrint?: string
}

const UserPremiumStatusComponent = ({
    premiumStatus,
    isLoading,
    statusError,
    fetchPremiumStatus,
    texts,
    lang
}: UserPremiumStatusComponentProps) => {
    const [portalLoading, setPortalLoading] = useState(false)

    const renderToggleButton = (buttonText: string) => (
        <TogglePremiumButton
            status={premiumStatus?.status}
            buttonText={buttonText}
            notLoggedIn={texts.notLoggedIn}
            userPremiumUnsubscribeConfirmationTitle={texts.userPremiumUnsubscribeConfirmationTitle}
            areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod={
                texts.areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod
            }
            keepPremium={texts.keepPremium}
            cancelPremium={texts.cancelPremium}
            premiumSubscriptionCancelledSuccessfully={texts.premiumSubscriptionCancelledSuccessfully}
            paymentProviderRedirect={texts.paymentProviderRedirect}
            errorResumingSubscription={texts.errorResumingSubscription}
            errorSubscribing={texts.errorSubscribing}
            errorUnsubscribing={texts.errorUnsubscribing}
            lang={lang}
        />
    )

    if (isLoading) {
        return (
            <div className='mt-8 p-6 flex justify-center'>
                <LoadingAnimation />
            </div>
        )
    }

    if (statusError) {
        return (
            <div className='mt-8 grid gap-5 rounded-xl p-6 border bg-secondary'>
                {/* @Todo Fix desctructive and few other theme variables */}
                <div className='flex items-center gap-2.5 text-red-500'>
                    <IconAlertTriangle />
                    <span className='text-lg font-semibold tracking-wide'>{texts.errorLoadingUserPremiumStatus}</span>
                </div>
                <p>{statusError}</p>
                <div className='mt-1'>
                    <Button onClick={() => fetchPremiumStatus()} className='w-full py-2 px-4 shadow '>
                        {texts.userPremiumRetryLoadingPremiumStatus}
                    </Button>
                </div>
            </div>
        )
    }

    if (!premiumStatus) {
        return (
            <div className='flex flex-col gap-5 rounded-xl'>
                <div className='flex items-center gap-2.5'></div>
                <div className='mt-1'>{renderToggleButton(texts.getPremium)}</div>
            </div>
        )
    }

    const statusConfig: Record<string, StatusConfig> = {
        default: {
            icon: <IconInfo color='text-blue-500' />,
            text: texts.userPremiumStatusNotActive,
            color: 'text-blue-500',
            containerClass: 'bg-gray-800 border-blue-900/50',
            expiryKey: 'expiryMessageInactive',
            subscriptionKey: 'subscriptionMessageInactive',
            buttonText: texts.getPremium
        },
        active: {
            icon: <IconCheckmark color='text-green-500' />,
            text: texts.userPremiumStatusActive,
            color: 'text-green-500',
            containerClass: 'bg-green-900/50 border-green-900/50',
            expiryKey: 'expiryMessageActive',
            subscriptionKey: 'subscriptionMessageActive',
            buttonText: texts.cancelPremium
        },
        expiring: {
            icon: <IconAlertTriangle className='text-amber-500' />,
            text: texts.userPremiumStatusExpiring,
            color: 'text-amber-500',
            containerClass: 'bg-amber-900/50 border-amber-900/50',
            expiryKey: 'expiryMessageExpiring',
            subscriptionKey: 'subscriptionMessageExpiring',
            buttonText: texts.buttonTextUpdatePayment
        },
        canceling: {
            icon: <IconInfo className='text-blue-500' />,
            text: texts.userPremiumStatusCanceled,
            color: 'text-blue-500',
            containerClass: 'bg-gray-900/100 border-blue-900/50',
            expiryKey: 'expiryMessageCanceling',
            subscriptionKey: 'subscriptionMessageCanceling',
            buttonText: texts.buttonTextRenewSubscription
        },
        canceled: {
            icon: <IconClose className='text-red-500' />,
            text: texts.userPremiumStatusCanceled,
            color: 'text-red-500',
            containerClass: 'bg-red-900/50 border-red-900/50',
            expiryKey: 'expiryMessageCanceled',
            subscriptionKey: 'subscriptionMessageCanceled',
            buttonText: texts.cancelPremium
        },
        expired: {
            icon: <IconClose className='text-red-500' />,
            text: texts.userPremiumStatusExpired,
            color: 'text-red-500',
            containerClass: 'bg-red-900/50 border-red-900/50',
            expiryKey: 'expiryMessageExpired',
            subscriptionKey: 'subscriptionMessageExpired',
            buttonText: texts.cancelPremium
        }
    }

    const config = statusConfig[premiumStatus.status] || statusConfig.default
    const formattedDate = premiumStatus.expires_at ? Date_formatVerbose(premiumStatus.expires_at) : 'Unknown date'
    const expiryMessage = texts[config.expiryKey as keyof typeof texts]
    const subscriptionMessage = texts[config.subscriptionKey as keyof typeof texts]
    // Show manage link for any status that might have a subscription (not just active/expiring/canceling)
    // This allows users to manage subscriptions even if they're canceled or expired
    const showManageLink = ['active', 'expiring', 'canceling', 'canceled', 'expired'].includes(premiumStatus.status)

    const openManageSubscription = async () => {
        setPortalLoading(true)
        try {
            const res = await fetch('/api/payments/create-portal-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ returnPath: `/${lang}/premium` }),
                credentials: 'include'
            })
            const j = await res.json()
            if (j?.data?.redirect_url) {
                window.location.href = j.data.redirect_url
                return
            }
            // Provide more specific error messages
            const errorMessage = j?.error || texts.errorLoadingUserPremiumStatus
            if (errorMessage.includes('No Stripe customer')) {
                toast.error('No subscription found. Please create a subscription first.')
            } else {
                toast.error(errorMessage)
            }
        } catch (error) {
            toast.error(texts.errorLoadingUserPremiumStatus)
        } finally {
            setPortalLoading(false)
        }
    }

    return (
        <div className={`mt-8 flex flex-col gap-5 rounded-xl p-6 border ${config.containerClass}`}>
            <div className='flex items-center gap-2.5 '>
                {config.icon}
                <span className={`text-lg font-semibold tracking-wide ${config.color}`}>{config.text}</span>
            </div>
            <div className='flex flex-col md:flex-row md:items-center gap-1.5'>
                <span className='text-muted-foreground text-center flex-wrap'>
                    {expiryMessage}{' '}
                    {premiumStatus.expires_at && (
                        <span className='whitespace-nowrap'>
                            <span className='font-bold text-foreground'>{formattedDate}</span>
                        </span>
                    )}
                </span>
            </div>
            <div>{renderToggleButton(config.buttonText)}</div>
            {showManageLink && (
                <button
                    type='button'
                    onClick={openManageSubscription}
                    disabled={portalLoading}
                    className='text-sm text-foreground hover:underline'
                >
                    {portalLoading ? '…' : 'Manage subscription'}
                </button>
            )}
            {subscriptionMessage && (
                <p className='text-xs font-medium text-foreground text-center leading-relaxed tracking-wide'>
                    {subscriptionMessage}
                </p>
            )}
        </div>
    )
}

export default UserPremiumStatusComponent
